import { useSuspenseQuery } from '@apollo/client';
import { Suspense } from 'react';
import type React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import {
  useParams,
  useLocation,
  useNavigate,
  Redirect,
} from '@xing-com/crate-core-router';
import { PageLayout } from '@xing-com/crate-people-directory-page-layout';
import { Pagination } from '@xing-com/crate-people-directory-pagination';
import { UserChunk } from '@xing-com/crate-people-directory-user-chunk';
import {
  UserMessage,
  UserMessageType,
} from '@xing-com/crate-people-directory-user-message';
import { useMediaListener, BREAKPOINTS } from '@xing-com/util';

import {
  USER_BAG_FILTERS_ID,
  SEO_URL_META_DATA,
  userBagCityRoutes,
} from './constants';
import { PeopleByCityUserBagPageDocument } from './graphql/user-bag-page-by-city.query.gql-types';
import { sanitizeNames } from './helper';
import {
  StyledHeadline,
  ContentPlaceholder,
  StyledFiltersForm,
} from './user-bag-page-by-city.styles';

type BasicPageLayoutProps = {
  index: boolean;
  seoTitle?: string;
  seoDescription?: string;
  seoUrl?: string;
  pageName: string;
  hidePageHeader?: boolean;
  breadcrumbs?: Array<{ title: string; url: string }>;
};

const BASE_URL = `/people/cities/`;

// Slug can be "bauer-michael" or "bauer-michael-p2"
const SLUG_AND_PAGE_REGEX =
  /((?<slugAndPageSlug>[a-zA-Z]+-[a-zA-Z]+)-p(?<pageFromUrl>[0-9]+))|((?<singleSlug>[a-zA-Z]+-[a-zA-Z]+))/;

const UserBagPageByCityInner: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const { country, city: citySlug, citySlugOrLetter } = useParams();
  const { slugAndPageSlug, pageFromUrl, singleSlug } =
    citySlugOrLetter.match(SLUG_AND_PAGE_REGEX).groups;
  const slug = slugAndPageSlug || singleSlug || '';
  const pageFromSlug = +pageFromUrl || 1;

  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);

  const currentFilters = {
    jobTitle: searchParams.get('jobtitle_query') || '',
  };

  const { data, error } = useSuspenseQuery(PeopleByCityUserBagPageDocument, {
    variables: {
      slug,
      city: citySlug.toLowerCase(),
      country,
      page: pageFromSlug,
      jobtitleQuery: currentFilters.jobTitle,
    },
    skip: !slug,
  });
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const onFiltersFormSubmit = (filters: Record<string, string>): void => {
    const searchParams = {
      ...(filters.jobTitle && { jobtitle_query: filters.jobTitle }),
    };

    navigate(
      `${BASE_URL}${country}/${citySlug}/${citySlugOrLetter}?jobtitle_query=${searchParams.jobtitle_query || ''}`
    );
  };

  const defaultPathParams = {
    city: citySlug.toLowerCase(),
    country,
    slug,
  };

  if (error || !data) {
    return (
      <Redirect to={`/people/cities/${country}/${citySlug.toLowerCase()}/`} />
    );
  }

  const { names, name, city, totalPages, page, count, breadcrumbs } =
    data?.peopleByCityUserBagPage || {};

  const sanitizedBreadcrumbs = breadcrumbs?.map((breadcrumb) => {
    return {
      title: breadcrumb?.title || '',
      url: `${breadcrumb?.url}` || '',
    };
  });

  const trackingContextData = currentFilters.jobTitle
    ? {
        PropSearchTerm: currentFilters.jobTitle,
      }
    : {};
  const seoTitle = formatMessage(
    { id: 'PD_CITIES_USER_BAG_PAGETITLE' },
    { display_name: name, city, total_count: count }
  );
  const seoDescription = formatMessage(
    { id: 'PD_CITIES_USER_BAG_META_DESCRIPTION' },
    { display_name: name, count, city }
  );
  const fullPageLayoutProps = {
    ...basicPageLayoutProps,
    seoTitle,
    seoDescription,
    trackingContextData,
    index: true,
    seoUrl: `${SEO_URL_META_DATA}cities/${country}/${citySlug.toLowerCase()}/${slug}${page === 1 ? '' : `-p${page}`}`,
    hidePageHeader: true,
    breadcrumbs: sanitizedBreadcrumbs,
  };

  const filtersFormParams = [
    {
      id: USER_BAG_FILTERS_ID.jobTitle,
      labelKey: 'PD_USER_BAG_SEARCH_OCCUPATION_LABEL',
      helperTextKey: 'PD_USER_BAG_SEARCH_OCCUPATION_INPUT_PLACEHOLDER',
      value: currentFilters.jobTitle,
    },
  ];
  const filtersForm = (
    <StyledFiltersForm
      filters={filtersFormParams}
      onSubmit={onFiltersFormSubmit}
      submitLabelKey="PD_USER_BAG_SEARCH_SUBMIT_BUTTON"
    />
  );

  const headlineMessage = (
    <FormattedMessage
      id="PD_CITY_USER_BAG_HEADLINE"
      values={{ count, display_name: name, city }}
    />
  );

  if (!data?.peopleByCityUserBagPage || !names || !names.length) {
    if (page && page > 1) {
      return (
        <Redirect
          to={`/people/cities/${country}/${citySlug.toLowerCase()}/${slug}`}
        />
      );
    } else {
      return (
        <PageLayout {...fullPageLayoutProps}>
          {filtersForm}
          <UserMessage type={UserMessageType.emptyUserBags} />
        </PageLayout>
      );
    }
  }

  return (
    <PageLayout {...fullPageLayoutProps}>
      <StyledHeadline size={isDesktop ? 'xxlarge' : 'large'}>
        {headlineMessage}
      </StyledHeadline>
      {filtersForm}
      {names ? (
        <UserChunk names={sanitizeNames(names)} fullWidthLayout />
      ) : null}
      {slug && page && totalPages && totalPages > 1 && (
        <Pagination
          currentPage={page}
          pagesNumber={totalPages || 0}
          route={userBagCityRoutes.USER_BAG_PAGE_PAGINATED}
          routeFirstPage={userBagCityRoutes.USER_BAG_PAGE}
          routeParams={defaultPathParams}
          filterQuery={currentFilters.jobTitle ? currentFilters : {}}
        />
      )}
    </PageLayout>
  );
};

const LoadingStateFallback: React.FC<BasicPageLayoutProps> = (
  basicPageLayoutProps
) => {
  const breakpoint = useMediaListener();
  const isDesktop = breakpoint === BREAKPOINTS.desktop;

  return (
    <PageLayout {...basicPageLayoutProps}>
      <ContentPlaceholder>
        <StyledHeadline size={isDesktop ? 'xxlarge' : 'large'}>
          ...
        </StyledHeadline>
      </ContentPlaceholder>
    </PageLayout>
  );
};

export const UserBagPageByCity: React.FC = () => {
  const { country, city: citySlug, citySlugOrLetter } = useParams();
  const { slugAndPageSlug, singleSlug } =
    citySlugOrLetter.match(SLUG_AND_PAGE_REGEX).groups;
  const slug = slugAndPageSlug || singleSlug || '';
  const letter = slug[0].toLowerCase();

  const basicPageLayoutProps = {
    index: true,
    pageName: `/user_bag/cities/${country}/${citySlug.toLowerCase()}/${letter}`,
  };

  return (
    <ErrorBoundary
      onError={() => (
        <Redirect to={`/people/cities/${country}/${citySlug.toLowerCase()}/`} />
      )}
    >
      <Suspense fallback={<LoadingStateFallback {...basicPageLayoutProps} />}>
        <UserBagPageByCityInner {...basicPageLayoutProps} />
      </Suspense>
    </ErrorBoundary>
  );
};
